import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, CheckCircle, Car, User, ChevronDown, ChevronUp,
  RefreshCw, Wrench, FileText, Plus, Trash2, Download,
  Printer, X, Receipt
} from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import {
  getAllBookings, updateBookingAdmin,
  getServices, getInventory,
  createInvoice
} from '../api';
import { downloadInvoicePdf, printInvoicePdf } from '../utils/PdfInvoice';
import './ServiceTrackerPage.css';

const STAGES       = ['Check-in', 'Inspection', 'In Service', 'QC Check', 'Ready'];
const ACTIVE_STATUSES = ['confirmed', 'in_progress', 'waiting'];
const STATUS_TO_STAGE = { confirmed: 0, waiting: 0, in_progress: 2 };
const ACCENT_COLORS   = ['#C9A46B','#A8823E','#4A7C59','#4A6B8A','#B05050','#7B5EA7','#2E7D8A'];
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

/* ══════════════════════════════════════════════════
   GENERATE BILL MODAL
══════════════════════════════════════════════════ */
function GenerateBillModal({ job, onClose, onCreated }) {
  const [dbServices,  setDbServices]  = useState([]);
  const [dbInventory, setDbInventory] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [invoice,     setInvoice]     = useState(null); // set after creation

  // Bill lines — pre-filled from booking, prices from DB
  const [services,  setServices]  = useState([]);
  const [parts,     setParts]     = useState([]);
  const [labour,    setLabour]    = useState(0);
  const [gstPct,    setGstPct]    = useState(18);

  /* Load service prices + inventory from DB */
  useEffect(() => {
    Promise.all([getServices(), getInventory()])
      .then(([svcs, inv]) => {
        setDbServices(svcs  || []);
        setDbInventory(inv  || []);

        // Pre-fill services from booking with DB prices
        const bookingServices = Array.isArray(job.services) ? job.services : [];
        const prefilled = bookingServices.map(name => {
          const found = (svcs || []).find(s =>
            s.name?.toLowerCase() === name.toLowerCase()
          );
          return {
            name,
            unitPrice: found ? Number(found.price || 0) : 0,
            amount:    found ? Number(found.price || 0) : 0,
          };
        });
        setServices(prefilled);
      })
      .catch(e => console.error('Failed to load DB data:', e.message))
      .finally(() => setLoading(false));
  }, [job.services]);

  /* Totals */
  const servicesTotal      = services.reduce((s, sv) => s + Number(sv.amount || 0), 0);
  const partsTotal         = parts.reduce((s, p) => s + Number(p.lineTotal || 0), 0);
  // Spare parts GST is always 18% (fixed statutory rate)
  const partsGst           = Math.round(partsTotal * 18 / 100);
  // Services + labour GST uses the configurable gstPct
  const servicesLabourBase = servicesTotal + Number(labour || 0);
  const servicesGst        = Math.round(servicesLabourBase * gstPct / 100);
  const gstAmount          = servicesGst + partsGst;
  const subtotal           = servicesTotal + partsTotal + Number(labour || 0);
  const grandTotal         = subtotal + gstAmount;

  /* Service handlers */
  const updateSvc = (i, field, val) => setServices(prev => prev.map((s, idx) => {
    if (idx !== i) return s;
    const updated = { ...s, [field]: field === 'unitPrice' || field === 'amount' ? Number(val) : val };
    if (field === 'unitPrice') updated.amount = Number(val);
    return updated;
  }));
  const removeSvc = (i) => setServices(prev => prev.filter((_, idx) => idx !== i));
  const addSvc    = ()  => setServices(prev => [...prev, { name: '', unitPrice: 0, amount: 0 }]);

  /* Part handlers */
  const addPart = () => setParts(prev => [...prev, { name: '', qty: 1, unitPrice: 0, lineTotal: 0 }]);
  const removePart = (i) => setParts(prev => prev.filter((_, idx) => idx !== i));
  const updatePart = (i, field, val) => setParts(prev => prev.map((p, idx) => {
    if (idx !== i) return p;
    let updated = { ...p, [field]: field === 'qty' || field === 'unitPrice' ? Number(val) : val };
    // Auto-fill price from inventory
    if (field === 'name') {
      const found = dbInventory.find(it => it.name === val);
      if (found) updated.unitPrice = Number(found.unitPrice || 0);
    }
    updated.lineTotal = (updated.qty || 1) * (updated.unitPrice || 0);
    return updated;
  }));

  /* Create invoice via API */
  const handleCreate = async () => {
    if (services.length === 0) { alert('Add at least one service.'); return; }
    setSaving(true);
    try {
      const payload = {
        bookingId:    job.id,
        customerId:   job.userId,
        customerName: job.ownerName  || job.customer || '',
        customerPhone: job.phone     || '',
        customerEmail: job.email     || '',
        vehicleDisplay: job.vehicleMake
          ? `${job.vehicleMake} ${job.vehicleModel || ''}`.trim()
          : '',
        regNumber:    job.regNumber  || '',
        fuelType:     job.fuelType   || '',
        mechanicName: job.mechanicName || job.mechanic || '',
        serviceDate:  job.date || new Date().toISOString().split('T')[0],
        services:     services.map(s => ({
          name:      s.name,
          unitPrice: s.unitPrice,
          amount:    s.amount,
        })),
        spareParts:   parts.map(p => ({
          name:      p.name,
          qty:       p.qty,
          unitPrice: p.unitPrice,
          lineTotal: p.lineTotal,
        })),
        labourCharges: Number(labour || 0),
        gstPercent:    gstPct,
      };
      const created = await createInvoice(payload);
      setInvoice(created);
      onCreated(created);
    } catch (e) {
      alert('Failed to create invoice: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  /* ── Render: Invoice Preview (after creation) ── */
  if (invoice) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal bill-modal" onClick={e => e.stopPropagation()}>
          <div className="modal__header">
            <div>
              <h3 className="modal__title">{invoice.id}</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {invoice.customerName} · {invoice.vehicleDisplay}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => downloadInvoicePdf(invoice)}>
                <Download size={13} /> Download PDF
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => printInvoicePdf(invoice)}>
                <Printer size={13} /> Print
              </button>
              <button className="modal__close" onClick={onClose}><X size={18} /></button>
            </div>
          </div>

          {/* Invoice preview */}
          <div className="inv-preview">
            <div className="inv-preview__band">
              <div>
                <div className="inv-preview__brand">AutoElite</div>
                <div className="inv-preview__addr">12, GT Road, Ludhiana · hello@autoelite.in</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="inv-preview__id">{invoice.id}</div>
                <div className="inv-preview__date">{invoice.serviceDate}</div>
                <span className="badge badge-warning">PENDING</span>
              </div>
            </div>

            <div className="inv-preview__parties">
              <div>
                <p className="appt-detail-label">Bill To</p>
                <p className="appt-detail-value">{invoice.customerName}</p>
                <p className="appt-detail-sub">{invoice.customerPhone}</p>
                <p className="appt-detail-sub">{invoice.customerEmail}</p>
              </div>
              <div>
                <p className="appt-detail-label">Vehicle</p>
                <p className="appt-detail-value">{invoice.vehicleDisplay}</p>
                <p className="appt-detail-sub">Reg: {invoice.regNumber}</p>
                {invoice.mechanicName && <p className="appt-detail-sub">Mechanic: {invoice.mechanicName}</p>}
              </div>
            </div>

            <table className="inv-preview__table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ textAlign: 'center' }}>Qty</th>
                  <th style={{ textAlign: 'right' }}>Unit Price</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.services || []).map((s, i) => (
                  <tr key={i}>
                    <td>{s.name} <span className="inv-tag">Service</span></td>
                    <td style={{ textAlign: 'center' }}>1</td>
                    <td style={{ textAlign: 'right' }}>{inr(s.unitPrice)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(s.amount)}</td>
                  </tr>
                ))}
                {(invoice.spareParts || []).map((p, i) => (
                  <tr key={i}>
                    <td>{p.name} <span className="inv-tag inv-tag--part">Part</span></td>
                    <td style={{ textAlign: 'center' }}>{p.qty}</td>
                    <td style={{ textAlign: 'right' }}>{inr(p.unitPrice)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(p.lineTotal)}</td>
                  </tr>
                ))}
                {Number(invoice.labourCharges || 0) > 0 && (
                  <tr>
                    <td>Labour Charges</td>
                    <td style={{ textAlign: 'center' }}>1</td>
                    <td style={{ textAlign: 'right' }}>{inr(invoice.labourCharges)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(invoice.labourCharges)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="inv-preview__totals">
              <div className="inv-preview__total-row"><span>Subtotal</span><span>{inr(invoice.subtotal)}</span></div>
              <div className="inv-preview__total-row" style={{ fontSize:12, color:'var(--text-muted)' }}>
                <span>GST on Services &amp; Labour ({invoice.gstPercent}%)</span>
                <span>{inr(Math.round((Number(invoice.subtotal||0) - (invoice.spareParts||[]).reduce((s,p)=>s+Number(p.lineTotal||0),0)) * (invoice.gstPercent||18) / 100))}</span>
              </div>
              {(invoice.spareParts||[]).length > 0 && (
                <div className="inv-preview__total-row" style={{ fontSize:12, color:'var(--text-muted)' }}>
                  <span>GST on Spare Parts (18%)</span>
                  <span>{inr(Math.round((invoice.spareParts||[]).reduce((s,p)=>s+Number(p.lineTotal||0),0) * 0.18))}</span>
                </div>
              )}
              <div className="inv-preview__total-row inv-preview__total-row--grand">
                <span>Grand Total</span><span>{inr(invoice.grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Render: Bill Builder ── */
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal bill-modal" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <h3 className="modal__title">Generate Invoice</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {job.id} · {job.ownerName} · {job.vehicleMake} {job.vehicleModel}
            </p>
          </div>
          <button className="modal__close" onClick={onClose}><X size={18} /></button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            <div className="appt-spinner" />
            <p style={{ marginTop: 12, fontSize: 13 }}>Fetching prices from database…</p>
          </div>
        ) : (
          <div className="bill-builder">

            {/* ── Services ── */}
            <div className="bill-section">
              <div className="bill-section__hdr">
                <span className="bill-section__title">Services</span>
                <button className="btn btn-sm btn-outline" onClick={addSvc}>
                  <Plus size={12} /> Add Service
                </button>
              </div>
              <div className="bill-table-wrap">
                <table className="bill-table">
                  <thead>
                    <tr><th>Service Name</th><th>Unit Price (₹)</th><th>Amount (₹)</th><th /></tr>
                  </thead>
                  <tbody>
                    {services.map((s, i) => (
                      <tr key={i}>
                        <td>
                          <select className="input input--sm"
                            value={s.name}
                            onChange={e => {
                              const name  = e.target.value;
                              const found = dbServices.find(ds => ds.name === name);
                              const price = found ? Number(found.price || 0) : s.unitPrice;
                              setServices(prev => prev.map((sv, idx) =>
                                idx === i ? { name, unitPrice: price, amount: price } : sv
                              ));
                            }}>
                            <option value="">Select service…</option>
                            {dbServices.map(ds => (
                              <option key={ds.id} value={ds.name}>{ds.name}</option>
                            ))}
                            {/* Show booking service even if not in catalogue */}
                            {s.name && !dbServices.find(ds => ds.name === s.name) && (
                              <option value={s.name}>{s.name}</option>
                            )}
                          </select>
                        </td>
                        <td>
                          <input className="input input--sm" type="number" min="0"
                            value={s.unitPrice || ''}
                            onChange={e => updateSvc(i, 'unitPrice', e.target.value)} />
                        </td>
                        <td>
                          <input className="input input--sm" type="number" min="0"
                            value={s.amount || ''}
                            onChange={e => updateSvc(i, 'amount', e.target.value)} />
                        </td>
                        <td>
                          <button className="tbl-btn tbl-btn--danger" onClick={() => removeSvc(i)}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Spare Parts ── */}
            <div className="bill-section">
              <div className="bill-section__hdr">
                <span className="bill-section__title">Spare Parts</span>
                <button className="btn btn-sm btn-outline" onClick={addPart}>
                  <Plus size={12} /> Add Part
                </button>
              </div>
              {parts.length > 0 && (
                <div className="bill-table-wrap">
                  <table className="bill-table">
                    <thead>
                      <tr><th>Part Name</th><th>Qty</th><th>Unit Price (₹)</th><th>Total</th><th /></tr>
                    </thead>
                    <tbody>
                      {parts.map((p, i) => (
                        <tr key={i}>
                          <td>
                            <select className="input input--sm"
                              value={p.name}
                              onChange={e => updatePart(i, 'name', e.target.value)}>
                              <option value="">Select part…</option>
                              {dbInventory.map(it => (
                                <option key={it.id} value={it.name}>
                                  {it.name} — {inr(it.unitPrice)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input className="input input--sm" type="number" min="1" style={{ width: 70 }}
                              value={p.qty}
                              onChange={e => updatePart(i, 'qty', e.target.value)} />
                          </td>
                          <td>
                            <input className="input input--sm" type="number" min="0"
                              value={p.unitPrice || ''}
                              onChange={e => updatePart(i, 'unitPrice', e.target.value)} />
                          </td>
                          <td style={{ fontWeight: 600, color: 'var(--accent)' }}>
                            {inr(p.lineTotal)}
                          </td>
                          <td>
                            <button className="tbl-btn tbl-btn--danger" onClick={() => removePart(i)}>
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {parts.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>
                  No spare parts added. Click "Add Part" to include inventory items.
                </p>
              )}
            </div>

            {/* ── Labour & GST ── */}
            <div className="bill-section bill-section--inline">
              <div>
                <label className="label">Labour Charges (₹)</label>
                <input className="input" type="number" min="0" placeholder="0"
                  value={labour || ''}
                  onChange={e => setLabour(Number(e.target.value))} />
              </div>
              <div>
                <label className="label">GST %</label>
                <input className="input" type="number" min="0" max="100"
                  value={gstPct}
                  onChange={e => setGstPct(Number(e.target.value))} />
              </div>
            </div>

            {/* ── Live Totals ── */}
            <div className="bill-totals">
              <div className="bill-totals__row"><span>Services</span><span>{inr(servicesTotal)}</span></div>
              <div className="bill-totals__row"><span>Spare Parts</span><span>{inr(partsTotal)}</span></div>
              <div className="bill-totals__row"><span>Labour</span><span>{inr(labour)}</span></div>
              <div className="bill-totals__row" style={{ color:'var(--text-muted)', fontSize:12 }}>
                <span>GST on Services &amp; Labour ({gstPct}%)</span><span>{inr(servicesGst)}</span>
              </div>
              {partsTotal > 0 && (
                <div className="bill-totals__row" style={{ color:'var(--text-muted)', fontSize:12 }}>
                  <span>GST on Spare Parts (18%)</span><span>{inr(partsGst)}</span>
                </div>
              )}
              <div className="bill-totals__row bill-totals__row--grand">
                <span>Grand Total</span><span>{inr(grandTotal)}</span>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={saving || services.length === 0} onClick={handleCreate}>
                <Receipt size={14} />
                {saving ? 'Generating…' : 'Generate Invoice'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN TRACKER PAGE
══════════════════════════════════════════════════ */
export default function ServiceTrackerPage() {
  const [jobs,       setJobs]      = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [expanded,   setExpanded]  = useState(null);
  const [saving,     setSaving]    = useState({});
  const [billJob,    setBillJob]   = useState(null);   // job to generate bill for
  const [readyJobs,  setReadyJobs] = useState({});     // { [jobId]: invoiceData }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all    = await getAllBookings();
      const active = all.filter(b =>
        ACTIVE_STATUSES.includes(b.status) || b.status === 'completed'
      );
      const withMeta = active.map((b, i) => ({
        ...b,
        _color: ACCENT_COLORS[i % ACCENT_COLORS.length],
        _stage: (b.progressStage != null && b.progressStage > 0)
          ? b.progressStage
          : (STATUS_TO_STAGE[b.status] ?? 0),
      }));
      // Sort newest first — Ready jobs (stage 4) at top, then by createdAt / date desc
      const trackerJobs = withMeta
        .filter(j => ACTIVE_STATUSES.includes(j.status) || j._stage >= 4)
        .slice()
        .sort((a, b) => {
          // Ready (stage 4) jobs come first so admin can generate bill immediately
          if (b._stage !== a._stage && (a._stage === 4 || b._stage === 4)) {
            return b._stage - a._stage;
          }
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          if (tb !== ta) return tb - ta;
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          if (db !== da) return db - da;
          return (b.id || '').localeCompare(a.id || '');
        });
      setJobs(trackerJobs);
    } catch (e) {
      console.error('Failed to load tracker:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const advanceStage = async (job) => {
    if (job._stage >= 4) return;
    const newStage  = job._stage + 1;
    // At stage 4 (Ready) we set status = completed
    const newStatus = newStage >= 4 ? 'completed' : newStage >= 2 ? 'in_progress' : 'confirmed';
    setSaving(s => ({ ...s, [job.id]: true }));
    try {
      await updateBookingAdmin(job.id, { progressStage: newStage, status: newStatus });
      setJobs(prev => prev.map(j => j.id === job.id
        ? { ...j, _stage: newStage, status: newStatus, progressStage: newStage }
        : j
      ));
    } catch (e) {
      alert('Update failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(s => ({ ...s, [job.id]: false }));
    }
  };

  const toggleExpand = (id) => setExpanded(e => e === id ? null : id);
  const stageCount   = (s)  => jobs.filter(j => j._stage === s).length;
  const getName      = b    => b.ownerName    || '—';
  const getVehicle   = b    => b.vehicleMake ? `${b.vehicleMake} ${b.vehicleModel || ''}`.trim() : '—';
  const getMech      = b    => b.mechanicName || 'Unassigned';
  const getService   = b    => Array.isArray(b.services) ? b.services.join(', ') : '—';

  return (
    <>
      <TopBar title="Service Tracker" subtitle="Live job status for all active vehicles" />
      <div className="admin-content">

        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Active Jobs</h1>
            <p>{jobs.length} vehicle{jobs.length !== 1 ? 's' : ''} in service</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Stage summary bar */}
        <div className="tracker-stages stagger">
          {STAGES.map((stage, i) => (
            <div key={stage}
              className={`tracker-stage card afu ${stageCount(i) > 0 ? 'tracker-stage--active' : ''}`}>
              <span className="tracker-stage__count">{stageCount(i)}</span>
              <span className="tracker-stage__name">{stage}</span>
              <div className="tracker-stage__bar">
                <div className="tracker-stage__fill"
                  style={{ width: jobs.length ? `${(stageCount(i) / jobs.length) * 100}%` : '0%' }} />
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            <div className="appt-spinner" />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading active jobs…</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="appt-empty">
            <Wrench size={40} />
            <p>No active jobs right now. Confirmed bookings appear here automatically.</p>
          </div>
        ) : (
          <div className="tracker-list">
            {jobs.map(job => {
              const isOpen     = expanded === job.id;
              const progress   = (job._stage / (STAGES.length - 1)) * 100;
              const isSaving   = saving[job.id];
              const isReady    = job._stage >= 4;
              const hasInvoice = readyJobs[job.id];

              return (
                <div key={job.id}
                  className={`tracker-job card ${isReady ? 'tracker-job--done' : ''}`}>
                  <div className="tracker-job__main" onClick={() => toggleExpand(job.id)}>
                    <div className="tracker-job__accent" style={{ background: job._color }} />

                    {/* Info */}
                    <div className="tracker-job__info">
                      <div className="tracker-job__top">
                        <span className="tracker-job__id">{job.id}</span>
                        {isReady
                          ? <span className="badge badge-success"><CheckCircle size={10} /> Ready</span>
                          : job.status === 'in_progress'
                            ? <span className="badge badge-warning"><Clock size={10} /> In Service</span>
                            : job.status === 'confirmed'
                              ? <span className="badge badge-info"><CheckCircle size={10} /> Confirmed</span>
                              : <span className="badge badge-neutral"><Clock size={10} /> Waiting</span>
                        }
                      </div>
                      <div className="tracker-job__vehicle">
                        <Car size={13} /> {getVehicle(job)} · {job.regNumber || '—'}
                      </div>
                      <p className="tracker-job__service">{getService(job)}</p>
                    </div>

                    {/* Progress */}
                    <div className="tracker-job__progress-col">
                      <div className="tracker-job__stages">
                        {STAGES.map((s, i) => (
                          <div key={s}
                            className={`stage-pill ${i < job._stage ? 'done' : i === job._stage ? 'current' : ''}`}
                            title={s}>
                            {i < job._stage ? <CheckCircle size={10} /> : <span>{i + 1}</span>}
                          </div>
                        ))}
                      </div>
                      <div className="tracker-progress-bar">
                        <div className="tracker-progress-fill"
                          style={{ width: `${progress}%`, background: isReady ? 'var(--success)' : job._color }} />
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                        {STAGES[Math.min(job._stage, 4)]}
                      </p>
                    </div>

                    {/* Meta */}
                    <div className="tracker-job__meta">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                        <User size={12} style={{ color: 'var(--accent)' }} /> {getMech(job)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
                        <Clock size={12} /> {job.date} · {job.timeSlot}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
                        <User size={12} /> {getName(job)}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="tracker-job__actions" onClick={e => e.stopPropagation()}>
                      {!isReady ? (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={isSaving}
                          onClick={() => advanceStage(job)}>
                          {isSaving ? '…' : `→ ${STAGES[Math.min(job._stage + 1, 4)]}`}
                        </button>
                      ) : hasInvoice ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm btn-outline"
                            onClick={() => downloadInvoicePdf(hasInvoice)}>
                            <Download size={12} />
                          </button>
                          <button className="btn btn-sm btn-outline"
                            onClick={() => printInvoicePdf(hasInvoice)}>
                            <Printer size={12} />
                          </button>
                          <span className="badge badge-success" style={{ padding: '6px 10px', fontSize: 11 }}>
                            <FileText size={10} /> Invoiced
                          </span>
                        </div>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setBillJob(job)}>
                          <Receipt size={13} /> Generate Bill
                        </button>
                      )}
                    </div>

                    <button className="tracker-expand-btn">
                      {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                  </div>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div className="tracker-job__detail">
                      <div className="tracker-detail-grid">
                        <div>
                          <p className="appt-detail-label">Customer</p>
                          <p className="appt-detail-value">{getName(job)}</p>
                          <p className="appt-detail-sub">{job.phone || '—'}</p>
                          <p className="appt-detail-sub">{job.email || '—'}</p>
                        </div>
                        <div>
                          <p className="appt-detail-label">Vehicle</p>
                          <p className="appt-detail-value">{getVehicle(job)}</p>
                          <p className="appt-detail-sub">Reg: {job.regNumber || '—'}</p>
                          {job.fuelType && <p className="appt-detail-sub">Fuel: {job.fuelType}</p>}
                        </div>
                        <div>
                          <p className="appt-detail-label">Mechanic</p>
                          <p className="appt-detail-value">{getMech(job)}</p>
                        </div>
                        <div>
                          <p className="appt-detail-label">Scheduled</p>
                          <p className="appt-detail-value">{job.date}</p>
                          <p className="appt-detail-sub">{job.timeSlot}</p>
                        </div>
                      </div>
                      {job.additionalNotes && (
                        <div style={{ marginTop: 12 }}>
                          <p className="appt-detail-label">Customer Notes</p>
                          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                            {job.additionalNotes}
                          </p>
                        </div>
                      )}
                      {isReady && !hasInvoice && (
                        <div style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(201,164,107,0.08)', borderRadius: 8, border: '1px solid var(--border-accent)' }}>
                          <p style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                            Vehicle is ready. Generate the invoice to complete the job.
                          </p>
                          <button className="btn btn-primary btn-sm" onClick={() => setBillJob(job)}>
                            <Receipt size={13} /> Generate Bill
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Generate Bill  */}
      {billJob && (
        <GenerateBillModal
          job={billJob}
          onClose={() => setBillJob(null)}
          onCreated={(inv) => {
            setReadyJobs(prev => ({ ...prev, [billJob.id]: inv }));
            
          }}
        />
      )}
    </>
  );
}